import { DateTime } from 'luxon';

import type { UpdateYouthAdvancementPayload } from '@appTypes/esb';

import type { SupportedAdvancementType } from './types';

/**
 * The four form fields that drive a save. On `values` (the target), a field may be
 * `undefined` when its Form.Item wasn't rendered (e.g. a page showing only the completion date).
 * That reads as "unchanged", never as unchecked/cleared, so we don't have to backfill hidden controls.
 * `initial` is the complete state the form opened with
 */
interface AdvancementSaveState {
  completionDate?: DateTime | null;
  approved?: boolean;
  counselorApproved?: boolean;
  awarded?: boolean;
}

interface BuildAdvancementSaveArgs {
  type: SupportedAdvancementType;
  /** Id of the leader recording the change */
  leaderUserId: number;
  organizationGuid?: string;
  /** Target state from the form */
  values: AdvancementSaveState;
  /** State the form opened with, to detect what changed and in which direction */
  initial: AdvancementSaveState;
  /** Injectable clock for tests */
  now?: DateTime;
}

/**
 * `update` puts the typed body; `unapprove` posts to `/advancements/unapprove` with
 * a status to step the advancement back down to
 */
export type AdvancementSaveResult =
  | { kind: 'update'; body: UpdateYouthAdvancementPayload }
  | { kind: 'unapprove'; newStatus: string };

const NEW_STATUS = {
  notComplete: 'Not Complete',
  completed: 'Completed',
  counselorApproved: 'Counselor Approved',
  leaderApproved: 'Leader Approved',
} as const;

/**
 * Builds the per-type save for one advancement from a form transition. A faithful,
 * typed port of the legacy `generateAdvancementBody`: checking a box (or setting the
 * date) produces an `update` with the fields for that operation; unchecking a box or
 * clearing the date produces an `unapprove` that steps the status down. Merit badges
 * award into `dateAwarded` (ranks/adventures/awards use `awarded`/`awardedDate`), and
 * completion lands in `dateEarned` for ranks/awards, `dateCompleted` otherwise
 */
export function buildAdvancementSave(
  args: BuildAdvancementSaveArgs,
): AdvancementSaveResult {
  const { type, leaderUserId, organizationGuid, values, initial, now } = args;

  const today = (now ?? DateTime.now()).toISODate() ?? '';
  const format = (date: DateTime) => date.toISODate() ?? '';
  const asDay = (date: DateTime | null) => (date ? format(date) : null);

  const isMeritBadge = type === 'meritBadges';
  const dateKey =
    type === 'ranks' || type === 'awards' ? 'dateEarned' : 'dateCompleted';

  // A field absent from `values` (its control wasn't rendered) can't have changed, so
  // treat undefined as "unchanged", NEVER as false/cleared, which would step the advancement down.
  const isCompletedChanged =
    values.completionDate !== undefined &&
    asDay(values.completionDate) !== asDay(initial.completionDate ?? null);
  const isApproveChanged =
    values.approved !== undefined && values.approved !== initial.approved;
  const isAwardedChanged =
    values.awarded !== undefined && values.awarded !== initial.awarded;
  const isCounselorApprovedChanged =
    values.counselorApproved !== undefined &&
    values.counselorApproved !== initial.counselorApproved;

  const date =
    values.completionDate !== undefined
      ? values.completionDate
      : initial.completionDate ?? null;
  const body: Record<string, unknown> = {};
  let newStatus: string | undefined;

  if (isAwardedChanged && date) {
    if (values.awarded) {
      if (isMeritBadge) body.dateAwarded = today;
      else {
        body.awarded = true;
        body.awardedDate = today;
      }
      body.awardedUserId = leaderUserId;
      if (isCompletedChanged) body[dateKey] = format(date);
    } else {
      newStatus = NEW_STATUS.leaderApproved;
    }
  }

  if (isApproveChanged && date) {
    if (values.approved) {
      body.leaderApprovedUserId = leaderUserId;
      body.leaderApprovedDate = today;
      if (isCompletedChanged) body[dateKey] = format(date);
    } else {
      newStatus =
        isMeritBadge && values.counselorApproved
          ? NEW_STATUS.counselorApproved
          : NEW_STATUS.completed;
    }
  }

  if (isMeritBadge && isCounselorApprovedChanged && date) {
    if (values.counselorApproved) {
      body.counselorApprovedDate = today;
      if (isCompletedChanged) body[dateKey] = format(date);
    } else {
      newStatus = NEW_STATUS.completed;
    }
  }

  if (isCompletedChanged && date) {
    body[dateKey] = format(date);
    body.markedCompletedDate = today;
  } else if (isCompletedChanged && !date) {
    newStatus = NEW_STATUS.notComplete;
  }

  if (newStatus) return { kind: 'unapprove', newStatus };

  if (organizationGuid) body.organizationGuid = organizationGuid;
  return { kind: 'update', body: body as UpdateYouthAdvancementPayload };
}
